import { useState, useEffect } from "react";
import { Building, Layers, PiggyBank } from "lucide-react";
import { cn } from "@/lib/utils";
import { useT } from "@/lib/translation";

interface BrokerageLogoProps {
  brokerage?: string | null;
  customAssetClass?: string | null; // for brokerage === 'custom', distinguishes PPR vs Certificado de Aforro etc.
  logoKey?: string | null; // for brokerage === 'manual' or 'custom', the logo the user picked (path under /logos/)
  className?: string; // Allow overriding size/classes
  size?: "sm" | "md" | "lg"; // Predefined sizes
}

const sizeClasses = {
  sm: "h-4 w-4",
  md: "h-5 w-5",
  lg: "h-12 w-12",
};

// Custom asset classes issued by a single known institution — reuses the company-logo
// asset-class fallback (logos/companies/CUSTOM/) since all holdings of that class share
// one issuer regardless of how each portfolio/holding is named.
const CUSTOM_ASSET_CLASS_LOGOS: Record<string, string> = {
  CERTIFICADO_AFORRO: "/logos/companies/CUSTOM/CTT.png",
};

// Brokerage logos are .svg by default (see the src fallback below). Entries here override
// that for brokerages whose logo asset isn't an svg.
const BROKERAGE_LOGO_OVERRIDES: Record<string, string> = {
  robinhood: "/logos/brokerages/robinhood.png",
};

export const BrokerageLogo = ({
  brokerage,
  customAssetClass,
  logoKey,
  className,
  size = "md",
}: BrokerageLogoProps) => {
  const [hasError, setHasError] = useState(false);
  const t = useT();

  // Reset error state if brokerage changes
  useEffect(() => {
    setHasError(false);
  }, [brokerage, customAssetClass, logoKey]);

  // Manual and custom portfolios have no brand logo of their own, so they show the picked
  // one. Without a pick (or when the file fails to load) a manual portfolio shows the same
  // neutral icon as "no brokerage" and a custom one its usual asset class logo or piggy bank.
  const isManual = brokerage === "manual";
  const canPickLogo = isManual || brokerage === "custom";
  const pickedLogo = canPickLogo && !hasError ? logoKey : undefined;

  if (!brokerage || (isManual && !pickedLogo)) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted rounded-md",
          sizeClasses[size],
          className,
        )}
        role="img"
        aria-label={
          isManual
            ? t("common:brokerageLogo.manualPortfolio")
            : t("common:brokerageLogo.noBrokerage")
        }
      >
        <Building
          className={cn(
            "text-muted-foreground",
            size === "sm" ? "h-3 w-3" : "h-4 w-4",
          )}
        />
      </div>
    );
  }

  if (brokerage === "Aggregate") {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-primary/10 rounded-md",
          sizeClasses[size],
          className,
        )}
        role="img"
        aria-label={t("common:brokerageLogo.allPortfolios")}
      >
        <Layers
          className={cn(
            "text-primary",
            // Same share of the box as the custom portfolio PiggyBank below.
            size === "sm" ? "h-3 w-3" : "h-3/5 w-3/5",
          )}
        />
      </div>
    );
  }

  if (pickedLogo) {
    return (
      <img
        src={`/logos/${pickedLogo}`}
        alt={t("common:brokerageLogo.portfolioLogo")}
        className={cn("object-contain", sizeClasses[size], className)}
        onError={() => setHasError(true)}
      />
    );
  }

  const assetClassLogo =
    brokerage === "custom" && customAssetClass
      ? CUSTOM_ASSET_CLASS_LOGOS[customAssetClass]
      : undefined;

  // Custom portfolios with no known single-institution logo (e.g. PPR, which can hold
  // funds from several institutions) — same PiggyBank fallback used for custom holdings.
  if (brokerage === "custom" && !assetClassLogo) {
    return (
      <div
        className={cn(
          "flex items-center justify-center bg-muted rounded-md",
          sizeClasses[size],
          className,
        )}
        role="img"
        aria-label={t("common:brokerageLogo.customPortfolio")}
      >
        <PiggyBank className="h-3/5 w-3/5 text-muted-foreground" />
      </div>
    );
  }

  if (hasError) {
    return (
      <img
        src="/placeholder.svg"
        alt={t("common:brokerageLogo.placeholder")}
        className={cn("object-contain", sizeClasses[size], className)}
      />
    );
  }

  return (
    <img
      src={
        assetClassLogo ||
        BROKERAGE_LOGO_OVERRIDES[brokerage.toLowerCase()] ||
        `/logos/brokerages/${brokerage.toLowerCase()}.svg`
      }
      alt={t("common:brokerageLogo.logo", { brokerage })}
      className={cn("object-contain", sizeClasses[size], className)}
      onError={() => setHasError(true)}
    />
  );
};
